/**
 * @typedef {import('@theme/slideshow').Slideshow} Slideshow
 */

/**
 * @param {HTMLElement | null | undefined} panel
 */
function resetSlideshows(panel) {
  if (!panel) return;

  /** @type {NodeListOf<Slideshow>} */
  const slideshows = panel.querySelectorAll('slideshow-component');
  slideshows.forEach((el) => {
    if (typeof el.select === 'function') {
      el.select(0, undefined, { animate: false });
    }
  });
}

/**
 * A custom element for creating an accessible tabbed interface from source HTML elements.
 * @extends {HTMLElement}
 */
class TabsComponent extends HTMLElement {
  constructor() {
    super();
    /**
     * @private
     * @type {HTMLButtonElement[]}
     */
    this.buttons = [];
    /**
     * @private
     * @type {HTMLElement[]}
     */
    this.panels = [];
    /**
     * @private
     * @type {number}
     */
    this.activeIndex = 0;

    /** @private @type {('pending'|'visible'|'hidden')[]} */
    this.tabState = [];

    /** @private @type {MutationObserver[]} */
    this.panelObservers = [];

    /** @private */
    this.pendingCount = 0;

    /** @private */
    this.hasCommittedUI = false;

    /**
     * The container for the tab buttons. Initialized in _createDOMStructure.
     * @private
     * @type {HTMLElement | null}
     */
    this.header = null;
    /**
     * The container for the tab panels. Initialized in _createDOMStructure.
     * @private
     * @type {HTMLElement | null}
     */
    this.panelsContainer = null;
  }

  connectedCallback() {
    if (this.dataset.tabsInitialized) return;
    this.dataset.tabsInitialized = 'true';

    /** @type {HTMLElement[]} */
    const sourceTabs = Array.from(this.querySelectorAll('.u-tab[data-tab]'));
    if (sourceTabs.length === 0) return;

    this._createDOMStructure();

    sourceTabs.forEach((tab, index) => {
      this._addTab(tab, index);
    });

    sourceTabs.forEach((tab) => tab.remove());

    if (this.pendingCount === 0 && !this.hasCommittedUI) {
      this._commitResolvedTabs();
    }

    // Initial activation will be resolved once async content settles
    this._ensureValidActiveTab();
  }

  /**
   * Creates the necessary header and panels container elements within the component.
   * @private
   */
  _createDOMStructure() {
    const headerWrapper = document.createElement('div');
    headerWrapper.className = 'u-tabs__header-wrapper';

    this.header = document.createElement('div');
    this.header.className = 'u-tabs__header';
    this.header.setAttribute('role', 'tablist');

    this.panelsContainer = document.createElement('div');
    this.panelsContainer.className = 'u-tabs__panels';

    headerWrapper.appendChild(this.header);
    this.appendChild(headerWrapper);
    this.appendChild(this.panelsContainer);
  }

  /**
   * Processes a source tab element to create and append the final button and panel.
   * @param {HTMLElement} sourceTabEl - The original `.u-tab` element to process.
   * @param {number} index - The index of the tab.
   * @private
   */
  _addTab(sourceTabEl, index) {
    /** @type {HTMLElement | null} */
    const panel = sourceTabEl.querySelector('[data-tab-panel]');
    if (!panel || !this.header || !this.panelsContainer) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'u-tabs__button';
    button.textContent =
      sourceTabEl.getAttribute('data-tab-title') || `Tab ${index + 1}`;

    this._setupAria(button, panel, index);

    const hasImmediateContent = this._panelHasContent(panel);

    button.hidden = !hasImmediateContent;
    button.disabled = !hasImmediateContent;
    panel.hidden = true;

    this.tabState[index] = hasImmediateContent ? 'visible' : 'pending';

    if (!hasImmediateContent) {
      this.pendingCount++;
    }

    button.addEventListener('click', () => this.activate(index));

    this.header.appendChild(button);
    this.panelsContainer.appendChild(panel);

    this.buttons.push(button);
    this.panels.push(panel);

    if (!hasImmediateContent) {
      this._observePanel(panel, index);
    }
  }

  /**
   * Sets up the necessary ARIA attributes to link a button and a panel for accessibility.
   * @param {HTMLButtonElement} button - The tab button element.
   * @param {HTMLElement} panel - The tab panel element.
   * @param {number} index - The index of the tab.
   * @private
   */
  _setupAria(button, panel, index) {
    const uniqueId = `tab-${
      this.id || Math.random().toString(36).substr(2, 9)
    }`;
    button.id = `${uniqueId}-btn-${index}`;
    panel.id = `${uniqueId}-panel-${index}`;

    button.setAttribute('role', 'tab');
    button.setAttribute('aria-controls', panel.id);
    panel.setAttribute('role', 'tabpanel');
    panel.setAttribute('aria-labelledby', button.id);
  }

  /**
   * Observe panel for async content
   * @private
   * @param {HTMLElement} panel
   * @param {number} index
   */
  _observePanel(panel, index) {
    const observer = new MutationObserver(() => {
      if (!this._panelHasContent(panel)) return;

      if (this.tabState[index] === 'pending') {
        this._resolveTab(index, true);
        observer.disconnect();
        return;
      }
      // Content appeared after timeout had already marked this tab hidden
      if (this.tabState[index] === 'hidden') {
        this.tabState[index] = 'visible';
        const btn = this.buttons[index];
        const pnl = this.panels[index];
        if (btn) {
          btn.hidden = false;
          btn.disabled = false;
        }
        if (pnl) pnl.hidden = this.activeIndex !== index;
        observer.disconnect();
      }
    });

    observer.observe(panel, {
      childList: true,
      subtree: true,
    });

    setTimeout(() => {
      if (this.tabState[index] === 'pending') {
        this._resolveTab(index, false);
      }
    }, 2500);

    this.panelObservers.push(observer);
  }

  /**
   * Defines what "real content" means
   * @private
   * @param {HTMLElement} panel
   * @returns {boolean}
   */
  _panelHasContent(panel) {
    return Boolean(
      panel.querySelector(
        'slideshow-slide, .c-simple-card, product-card-custom'
      )
    );
  }

  /**
   * @private
   * @param {number} index
   * @param {boolean} hasContent
   * @returns {void}
   */
  _resolveTab(index, hasContent) {
    if (this.tabState[index] !== 'pending') return;

    this.tabState[index] = hasContent ? 'visible' : 'hidden';
    this.pendingCount--;

    if (this.pendingCount === 0 && !this.hasCommittedUI) {
      this._commitResolvedTabs();
    }
  }

  /**
   * Applies resolved tab states to the DOM.
   * @private
   * @returns {void}
   */
  _commitResolvedTabs() {
    this.hasCommittedUI = true;

    for (let index = 0; index < this.tabState.length; index++) {
      const state = this.tabState[index];

      /** @type {HTMLButtonElement | undefined} */
      const button = this.buttons[index];

      /** @type {HTMLElement | undefined} */
      const panel = this.panels[index];

      if (!button || !panel) continue;

      if (state === 'visible') {
        button.hidden = false;
        button.disabled = false;
      } else {
        button.hidden = true;
        panel.hidden = true;
      }
    }

    this._activateFirstVisibleTab();
  }

  _activateFirstVisibleTab() {
    const firstVisible = this.tabState.findIndex(
      (state) => state === 'visible'
    );

    if (firstVisible !== -1) {
      this.activate(firstVisible, { isInitial: true });
    }
  }

  /**
   * Ensures a valid visible tab is active
   * @private
   */
  _ensureValidActiveTab() {
    const currentState = this.tabState[this.activeIndex];

    if (currentState === 'visible') return;

    const nextIndex = this.tabState.findIndex((state) => state === 'visible');

    if (nextIndex !== -1) {
      this.activate(nextIndex, { isInitial: true });
    }
  }

  /**
   * @param {number} index
   * @param {{ isInitial?: boolean }} options
   */
  activate(index, options = {}) {
    if (
      index < 0 ||
      index >= this.buttons.length ||
      this.tabState[index] !== 'visible'
    ) {
      return;
    }

    this.activeIndex = index;

    this.buttons.forEach((b, idx) => {
      const isSelected = idx === index;
      b.setAttribute('aria-selected', String(isSelected));
      b.tabIndex = isSelected ? 0 : -1;
    });

    this.panels.forEach((p, idx) => {
      p.hidden = idx !== index;
    });

    if (!options.isInitial) {
      resetSlideshows(this.panels[index]);
    }
  }
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}
